import { MemCommonMentionKind } from "@mem-labs/common-editor";
import type { MDSComboboxMention } from "@/mds/components/forms";
import { AppStore } from "@/store";

import { loadOrganizableNotes } from "@/actions/autoOrganizeNotes";
import { CollectionObservable } from "@/store/collections/CollectionObservable";
import { INoteObservable } from "@/store/note";
import { toastModule } from "@/modules/toast";

const loadOrganizableCollections = async ({
  collectionIds,
  store,
}: {
  collectionIds: string[];
  store: AppStore;
}): Promise<CollectionObservable[] | null> => {
  const collections: CollectionObservable[] = [];

  for (const collectionId of collectionIds) {
    const collection = await store.collections.getAsync(collectionId);

    if (collection) {
      collections.push(collection);
    }
  }

  if (collections.length === 0) {
    toastModule.triggerToast({
      content: <div>Unable to find selected collections.</div>,
    });

    return null;
  }

  return collections;
};

interface StartChatWithEntitiesArgs {
  noteIds?: string[];
  collectionIds?: string[];
  store: AppStore;
  overrideContext?: boolean;
}

export const startChatWithEntities = async ({
  noteIds,
  collectionIds,
  store,
}: StartChatWithEntitiesArgs): Promise<boolean> => {
  if (!store.floatingAgent.isMemAgentAccessEnabled) return false;

  const uniqueNoteIds = noteIds ? Array.from(new Set(noteIds)) : [];
  const uniqueCollectionIds = collectionIds ? Array.from(new Set(collectionIds)) : [];

  let notes: INoteObservable[] = [];
  let collections: CollectionObservable[] = [];

  if (uniqueNoteIds.length > 0) {
    const loadedNotes = await loadOrganizableNotes({ noteIds: uniqueNoteIds, store });
    if (loadedNotes) {
      notes = loadedNotes;
    }
  }

  if (uniqueCollectionIds.length > 0) {
    const loadedCollections = await loadOrganizableCollections({
      collectionIds: uniqueCollectionIds,
      store,
    });
    if (loadedCollections) {
      collections = loadedCollections;
    }
  }

  const hasEntities = notes.length > 0 || collections.length > 0;

  if (!hasEntities) {
    return false;
  }

  const mentions: MDSComboboxMention[] = [
    ...collections.map(collection => ({
      kind: MemCommonMentionKind.Collection as const,
      isExternal: false,
      collection_id: collection.id,
      collection_title: collection.title ?? "",
    })),
    ...notes.map(note => ({
      kind: MemCommonMentionKind.Note as const,
      isExternal: false,
      note_id: note.id,
      note_primary_label: note.title ?? "",
    })),
  ];

  store.floatingAgent.requestAttachComposerMentions(mentions);
  return true;
};
